import { showToast } from '@moniebook-web/utils';
import {
    getTokenVariable,
    logger,
    unsyncedSaleService,
} from '@register-ui/utils';
import { useLiveQuery } from 'dexie-react-hooks';
import { useEffect, useState } from 'react';
import { type BannerFill, type BannerVariant } from '../types';
import {
    UNSYNCED_SALES_SYNC_STATUS,
    type UnsyncedSalesSyncStatus,
} from './types';

export const UNSYNCED_SALES_SYNC_THRESHOLD = 1;
export const SUCCESSFUL_SYNC_BANNER_DURATION = 3000;

const UNSYNCED_SALES_BANNER_STYLE_MAP: Record<
    UnsyncedSalesSyncStatus,
    { variant: BannerVariant; fill?: BannerFill }
> = {
    [UNSYNCED_SALES_SYNC_STATUS.Idle]: { variant: 'error' },
    [UNSYNCED_SALES_SYNC_STATUS.Syncing]: {
        variant: 'warning',
        fill: 'ghost',
    },
    [UNSYNCED_SALES_SYNC_STATUS.Success]: {
        variant: 'success',
        fill: 'ghost',
    },
    [UNSYNCED_SALES_SYNC_STATUS.Error]: { variant: 'error' },
};

export function useUnsyncedSalesSyncIndicator() {
    const [status, setStatus] = useState<UnsyncedSalesSyncStatus>(
        UNSYNCED_SALES_SYNC_STATUS.Idle,
    );

    const unsyncedSalesCountLive = useLiveQuery(
        () => unsyncedSaleService.getCount(),
        [],
    );

    const unsyncedSalesCount = unsyncedSalesCountLive ?? 0;

    useEffect(() => {
        if (status !== UNSYNCED_SALES_SYNC_STATUS.Success) {
            return;
        }

        const timer = setTimeout(() => {
            setStatus(UNSYNCED_SALES_SYNC_STATUS.Idle);
        }, SUCCESSFUL_SYNC_BANNER_DURATION);

        return () => clearTimeout(timer);
    }, [status]);

    const { variant: unsyncedSalesVariant, fill: unsyncedSalesFill } =
        UNSYNCED_SALES_BANNER_STYLE_MAP[status];

    const hasBranchAuthToken = !!getTokenVariable();

    const showUnsyncedSalesBanner =
        hasBranchAuthToken &&
        (status !== UNSYNCED_SALES_SYNC_STATUS.Idle ||
            unsyncedSalesCount >= UNSYNCED_SALES_SYNC_THRESHOLD);

    const syncUnsyncedSales = async () => {
        if (!hasBranchAuthToken) {
            return;
        }

        setStatus(UNSYNCED_SALES_SYNC_STATUS.Syncing);

        const { hasFailure } = await unsyncedSaleService.syncPendingSales();

        if (hasFailure) {
            setStatus(UNSYNCED_SALES_SYNC_STATUS.Error);
            logger.error({
                eventName: 'unsynced-sales-sync-failed',
                message: `[unsynced-sale-sync] failed to sync ${unsyncedSalesCount} pending sale(s)`,
            });
            showToast(
                'error',
                'Unable to sync sales. Please check your internet.',
            );
            return;
        }

        setStatus(UNSYNCED_SALES_SYNC_STATUS.Success);
    };

    return {
        unsyncedSalesCount,
        unsyncedSalesStatus: status,
        unsyncedSalesVariant,
        unsyncedSalesFill,
        showUnsyncedSalesBanner,
        syncUnsyncedSales,
    };
}
